import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useLanguage, useTheme, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  LayoutDashboard, 
  Scissors, 
  Users, 
  UserCircle, 
  Calendar, 
  CalendarDays,
  Star,
  Settings,
  LogOut,
  Sun,
  Moon,
  Globe,
  Menu,
  X,
  Shield,
  BarChart3,
  Bell,
  Lock,
  MessageSquare,
  Eye,
  Sparkles,
  ScanLine
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../components/ui/sheet";
import { NotificationToggle } from "./PWAInstallBanner";
import { useTier } from "./FeatureGate";
import { useIsDentalTenant } from "../hooks/useIsDentalTenant";

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { hasFeature } = useTier();
  const isDental = useIsDentalTenant();

  // Different menu items based on role
  const getSalonAdminMenuItems = () => {
    const base = [
      { icon: LayoutDashboard, label: t("dashboard"), path: "/dashboard" },
      { icon: CalendarDays, label: t("calendar"), path: "/calendar" },
      { icon: Calendar, label: t("appointments"), path: "/appointments" },
      { icon: Scissors, label: t("services"), path: "/services" },
      { icon: Users, label: t("staff"), path: "/staff" },
      { icon: UserCircle, label: t("clients"), path: "/clients" },
    ];
    if (isDental) {
      // Dental tenants: dedicated Smart Prescription hub
      base.push({ icon: ScanLine, label: language === "it" ? "Ricette SSN" : "Prescriptions", path: "/dashboard/dental/prescriptions" });
    } else {
      // Non-dental tenants: still show entry so any client-attached prescriptions
      // are discoverable. Path remains the same dedicated dental hub.
      base.push({ icon: ScanLine, label: language === "it" ? "Ricette SSN" : "Prescriptions", path: "/dashboard/dental/prescriptions" });
    }
    base.push(
      { icon: MessageSquare, label: "WhatsApp", path: "/whatsapp", feature: "whatsapp_basic" },
      { icon: BarChart3, label: "Analytics", path: "/analytics", feature: "ltv_analytics" },
      { icon: Star, label: t("reviews"), path: "/reviews" },
      { icon: Sparkles, label: language === "it" ? "Smart Page" : "Smart Page", path: "/smartpage" },
      { icon: Settings, label: t("settings"), path: "/settings" },
    );
    return base;
  };

  const getSuperAdminMenuItems = () => [
    { icon: Shield, label: t("super_admin"), path: "/admin" },
    { icon: LayoutDashboard, label: t("dashboard"), path: "/dashboard" },
  ];

  const getSalesMenuItems = () => [
    { icon: LayoutDashboard, label: language === "it" ? "I miei Lead" : "My Leads", path: "/sales-dashboard" },
  ];

  const menuItems = user?.role === "super_admin" 
    ? getSuperAdminMenuItems() 
    : user?.role === "sales"
    ? getSalesMenuItems()
    : getSalonAdminMenuItems();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="/smartbooker-icon-48.png" 
            alt="SmartBooker" 
            className="h-8 w-8"
          />
          <span className="font-semibold text-lg">SmartBooker</span>
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <span className="font-medium">{user?.name?.[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Demo mode banner */}
      {user?.tenant_id === "tenant_demo_sales" && (
        <div className="mx-4 mt-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="demo-banner">
          <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> MODALITA DEMO
          </p>
          <p className="text-[10px] text-amber-600/70 dark:text-amber-400/60 mt-0.5">Account condiviso per presentazioni. Reset giornaliero.</p>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          const locked = item.feature && !hasFeature(item.feature);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`sidebar-link ${isActive ? "active" : ""} ${locked ? "opacity-60" : ""}`}
              data-testid={`nav-${item.path.replace("/", "")}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {locked && (
                <Lock className="w-3.5 h-3.5 ml-auto text-amber-500" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className="p-4 border-t border-border space-y-2">
        {/* Attiva Demo - Super Admin only */}
        {user?.role === "super_admin" && (
          <button
            id="seed-demo-btn"
            onClick={async () => {
              const btn = document.getElementById("seed-demo-btn");
              const origText = btn.innerText;
              btn.innerText = "Caricamento...";
              btn.disabled = true;
              try {
                const res = await axios.post(`${API}/demo/seed`, {}, { withCredentials: true });
                if (res.data.success) {
                  btn.innerText = "Demo Attivata!";
                  btn.style.borderColor = "#10b981";
                  btn.style.color = "#10b981";
                  toast.success("Demo account attivato!");
                } else {
                  btn.innerText = "Errore: " + (res.data.error || "sconosciuto");
                  toast.error(res.data.error || "Errore");
                }
              } catch (e) {
                const msg = e.response?.data?.detail || e.response?.data?.error || e.message || "Errore";
                btn.innerText = "Errore!";
                toast.error(msg);
              }
              setTimeout(() => { btn.innerText = origText; btn.disabled = false; btn.style.borderColor = ""; btn.style.color = ""; }, 3000);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20 transition-colors mb-2"
            data-testid="seed-demo-btn"
          >
            <Sparkles className="w-4 h-4" />
            Attiva Demo
          </button>
        )}
        {/* Notification Toggle */}
        <div className="mb-2">
          <NotificationToggle language={language} compact />
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeLanguage(language === "it" ? "en" : "it")}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-muted transition-colors"
            data-testid="sidebar-language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm">{language.toUpperCase()}</span>
          </button>
          <button
            onClick={toggleTheme}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-muted transition-colors"
            data-testid="sidebar-theme-toggle"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>{t("logout")}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border hidden lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
              alt="SmartBooker" 
              className="h-8 w-auto"
            />
          </Link>
          
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
